<template>
  <div class="api-workspace">
    <!-- 请求配置区 -->
    <div class="request-section">
      <div class="api-header">
        <el-input 
          v-model="localApi.name" 
          placeholder="接口名称"
          class="api-name-input"
          @change="handleUpdate"
        />
      </div>
      
      <div class="request-bar">
        <el-select v-model="localApi.method" style="width: 120px" @change="handleMethodChange">
          <el-option label="GET" value="GET" />
          <el-option label="POST" value="POST" />
          <el-option label="PUT" value="PUT" />
          <el-option label="DELETE" value="DELETE" />
          <el-option label="PATCH" value="PATCH" />
          <el-option label="WebSocket" value="WS" />
          <el-option label="STOMP" value="STOMP" />
        </el-select>
        <el-input
          v-model="localApi.url"
          placeholder="请输入请求 URL"
          class="url-input"
          @change="handleUpdate"
        />
        <template v-if="isWebSocketMethod">
          <el-button v-if="!wsConnected" type="primary" @click="connectWebSocket" :loading="loading">
            <el-icon><Promotion /></el-icon>
            连接
          </el-button>
          <el-button v-else type="danger" @click="disconnectWebSocket">
            <el-icon><CircleClose /></el-icon>
            断开
          </el-button>
        </template>
        <el-button v-else type="primary" @click="sendRequest" :loading="loading">
          <el-icon><Promotion /></el-icon>
          发送
        </el-button>
      </div>
      <div v-if="hasEnvVariables(localApi.url)" class="request-url">
        <el-tag type="info" size="small">实际请求地址</el-tag>
        <span class="resolved-url">{{ replaceEnvVariables(localApi.url) }}</span>
      </div>

      <!-- 请求参数标签页 -->
      <el-tabs v-model="activeTab" class="request-tabs">
        <el-tab-pane label="Params" name="params">
          <div class="params-table">
            <div class="table-header">
              <span class="col-key">Key</span>
              <span class="col-value">Value</span>
              <span class="col-type">Type</span>
              <span class="col-required">必填</span>
              <span class="col-desc">Description</span>
              <span class="col-action">操作</span>
            </div>
            <div
              v-for="(param, index) in localApi.params"
              :key="index"
              class="table-row"
            >
              <el-input v-model="param.key" placeholder="参数名" class="col-key" @change="handleUpdate" />
              <el-input v-model="param.value" placeholder="参数值" class="col-value" @change="handleUpdate" />
              <el-select v-model="param.type" placeholder="类型" class="col-type" @change="handleUpdate">
                <el-option label="string" value="string" />
                <el-option label="number" value="number" />
                <el-option label="boolean" value="boolean" />
                <el-option label="array" value="array" />
                <el-option label="object" value="object" />
              </el-select>
              <el-switch v-model="param.required" class="col-required" @change="handleUpdate" />
              <el-input v-model="param.description" placeholder="描述" class="col-desc" @change="handleUpdate" />
              <el-button type="danger" text @click="removeParam(index)">
                <el-icon><Delete /></el-icon>
              </el-button>
            </div>
            <el-button type="primary" text @click="addParam">
              <el-icon><Plus /></el-icon>
              添加参数
            </el-button>
          </div>
        </el-tab-pane>

        <el-tab-pane label="Headers" name="headers">
          <div class="params-table">
            <div class="table-header">
              <span class="col-key">Key</span>
              <span class="col-value">Value</span>
              <span class="col-desc">Description</span>
              <span class="col-action">操作</span>
            </div>
            <div
              v-for="(header, index) in localApi.headers"
              :key="index"
              class="table-row"
            >
              <el-input v-model="header.key" placeholder="Header 名" class="col-key" @change="handleUpdate" />
              <el-input v-model="header.value" placeholder="Header 值" class="col-value" @change="handleUpdate" />
              <el-input v-model="header.description" placeholder="描述" class="col-desc" @change="handleUpdate" />
              <el-button type="danger" text @click="removeHeader(index)">
                <el-icon><Delete /></el-icon>
              </el-button>
            </div>
            <el-button type="primary" text @click="addHeader">
              <el-icon><Plus /></el-icon>
              添加 Header
            </el-button>
          </div>
        </el-tab-pane>
        
        <el-tab-pane label="Body" name="body">
          <div class="body-section">
            <el-radio-group v-model="localApi.body.type" @change="handleBodyTypeChange">
              <el-radio value="none">none</el-radio>
              <el-radio value="json">JSON</el-radio>
              <el-radio value="form">form-data</el-radio>
              <el-radio value="x-www-form-urlencoded">x-www-form-urlencoded</el-radio>
              <el-radio value="raw">raw</el-radio>
              <el-radio value="binary">binary</el-radio>
            </el-radio-group>

            <!-- JSON / raw 文本输入 -->
            <el-input
              v-if="['json', 'raw'].includes(localApi.body.type)"
              v-model="localApi.body.content"
              type="textarea"
              :rows="8"
              placeholder="请求体内容"
              class="body-editor"
              @change="handleUpdate"
            />

            <!-- form-data / x-www-form-urlencoded 键值对编辑 -->
            <div v-if="['form', 'x-www-form-urlencoded'].includes(localApi.body.type)" class="params-table">
              <div class="table-header">
                <span class="col-key">Key</span>
                <span class="col-value">Value</span>
                <span class="col-type">类型</span>
                <span class="col-desc">描述</span>
                <span class="col-action">操作</span>
              </div>
              <div
                v-for="(item, index) in localApi.body.formData"
                :key="index"
                class="table-row"
              >
                <el-input v-model="item.key" placeholder="参数名" class="col-key" @change="handleUpdate" />
                <el-input v-if="item.type !== 'file'" v-model="item.value" placeholder="参数值" class="col-value" @change="handleUpdate" />
                <el-upload
                  v-else
                  class="col-value file-upload"
                  :auto-upload="false"
                  :on-change="(file) => handleFileChange(file, index)"
                  :show-file-list="false"
                >
                  <el-button type="primary" text size="small">
                    {{ item.fileName || '选择文件' }}
                  </el-button>
                </el-upload>
                <el-select v-if="localApi.body.type === 'form'" v-model="item.type" placeholder="类型" class="col-type" @change="handleUpdate">
                  <el-option label="text" value="text" />
                  <el-option label="file" value="file" />
                </el-select>
                <span v-else class="col-type">-</span>
                <el-input v-model="item.description" placeholder="描述" class="col-desc" @change="handleUpdate" />
                <el-button type="danger" text @click="removeFormDataItem(index)">
                  <el-icon><Delete /></el-icon>
                </el-button>
              </div>
              <el-button type="primary" text @click="addFormDataItem">
                <el-icon><Plus /></el-icon>
                添加参数
              </el-button>
            </div>

            <!-- binary 文件选择 -->
            <div v-if="localApi.body.type === 'binary'" class="binary-upload">
              <el-upload
                :auto-upload="false"
                :on-change="handleBinaryFileChange"
                :show-file-list="true"
                :limit="1"
              >
                <el-button type="primary">
                  <el-icon><Upload /></el-icon>
                  选择文件
                </el-button>
              </el-upload>
              <div v-if="localApi.body.fileName" class="selected-file">
                <el-tag type="success">已选择: {{ localApi.body.fileName }}</el-tag>
                <el-button type="danger" text size="small" @click="clearBinaryFile">
                  <el-icon><Delete /></el-icon>
                  清除
                </el-button>
              </div>
            </div>
          </div>
        </el-tab-pane>
        
        <el-tab-pane label="响应参数" name="responseParams">
          <div class="params-table">
            <div class="table-header">
              <span class="col-key">参数名</span>
              <span class="col-type">类型</span>
              <span class="col-required">必填</span>
              <span class="col-value">示例值</span>
              <span class="col-desc">描述</span>
              <span class="col-action">操作</span>
            </div>
            <div
              v-for="(param, index) in localApi.responseParams"
              :key="index"
              class="table-row"
            >
              <el-input v-model="param.key" placeholder="参数名" class="col-key" @change="handleUpdate" />
              <el-select v-model="param.type" placeholder="类型" class="col-type" @change="handleUpdate">
                <el-option label="string" value="string" />
                <el-option label="number" value="number" />
                <el-option label="boolean" value="boolean" />
                <el-option label="array" value="array" />
                <el-option label="object" value="object" />
              </el-select>
              <el-switch v-model="param.required" class="col-required" @change="handleUpdate" />
              <el-input v-model="param.value" placeholder="示例值" class="col-value" @change="handleUpdate" />
              <el-input v-model="param.description" placeholder="描述" class="col-desc" @change="handleUpdate" />
              <el-button type="danger" text @click="removeResponseParam(index)">
                <el-icon><Delete /></el-icon>
              </el-button>
            </div>
            <el-button type="primary" text @click="addResponseParam">
              <el-icon><Plus /></el-icon>
              添加响应参数
            </el-button>
          </div>
        </el-tab-pane>

        <el-tab-pane label="响应头" name="responseHeaders">
          <div class="params-table">
            <div class="table-header">
              <span class="col-key">Header 名</span>
              <span class="col-value">示例值</span>
              <span class="col-desc">描述</span>
              <span class="col-action">操作</span>
            </div>
            <div
              v-for="(header, index) in localApi.responseHeaders"
              :key="index"
              class="table-row"
            >
              <el-input v-model="header.key" placeholder="Header 名" class="col-key" @change="handleUpdate" />
              <el-input v-model="header.value" placeholder="示例值" class="col-value" @change="handleUpdate" />
              <el-input v-model="header.description" placeholder="描述" class="col-desc" @change="handleUpdate" />
              <el-button type="danger" text @click="removeResponseHeader(index)">
                <el-icon><Delete /></el-icon>
              </el-button>
            </div>
            <el-button type="primary" text @click="addResponseHeader">
              <el-icon><Plus /></el-icon>
              添加响应头
            </el-button>
          </div>
        </el-tab-pane>

        <el-tab-pane label="响应Body" name="responseBody">
          <div class="body-section">
            <el-radio-group v-model="localApi.responseBody.type" @change="handleUpdate">
              <el-radio value="none">none</el-radio>
              <el-radio value="json">JSON</el-radio>
              <el-radio value="xml">XML</el-radio>
              <el-radio value="raw">raw</el-radio>
            </el-radio-group>
            <el-input
              v-if="localApi.responseBody.type !== 'none'"
              v-model="localApi.responseBody.content"
              type="textarea"
              :rows="10"
              placeholder="响应示例内容"
              class="body-editor"
              @change="handleUpdate"
            />
          </div>
        </el-tab-pane>

        <el-tab-pane label="描述" name="description">
          <el-input
            v-model="localApi.description"
            type="textarea"
            :rows="10"
            placeholder="接口描述文档..."
            @change="handleUpdate"
          />
        </el-tab-pane>
      </el-tabs>
    </div>
    
    <!-- 响应展示区 -->
    <div class="response-section">
      <div class="response-header">
        <span class="response-title">响应结果</span>
        <div v-if="response" class="response-meta">
          <el-tag :type="getStatusType(response.status)">
            Status: {{ response.status }} {{ response.statusText }}
          </el-tag>
          <el-tag type="info">Time: {{ response.time }}ms</el-tag>
          <el-tag type="info">Size: {{ formatSize(response.size) }}</el-tag>
        </div>
      </div>
      
      <div class="response-content">
        <el-empty v-if="!response && !loading && !isWebSocketMethod" description="点击发送按钮查看响应" />

        <div v-if="loading" class="loading-state">
          <el-skeleton :rows="10" animated />
        </div>

        <!-- HTTP 响应 -->
        <div v-if="response && !isWebSocketMethod" class="response-data">
          <el-tabs v-model="responseTab">
            <el-tab-pane label="Body" name="body">
              <div class="response-body">
                <pre v-if="isJson(response.body)">{{ formatJson(response.body) }}</pre>
                <pre v-else>{{ response.body }}</pre>
              </div>
            </el-tab-pane>
            <el-tab-pane label="Headers" name="headers">
              <div class="response-headers">
                <div v-for="(value, key) in response.headers" :key="key" class="header-row">
                  <span class="header-key">{{ key }}:</span>
                  <span class="header-value">{{ value }}</span>
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>

        <!-- WebSocket 消息列表 -->
        <div v-if="isWebSocketMethod" class="ws-messages">
          <div class="ws-status">
            <el-tag :type="wsConnected ? 'success' : 'info'">
              {{ wsConnected ? '已连接' : '未连接' }}
            </el-tag>
          </div>

          <!-- STOMP 发送区域 -->
          <div v-if="localApi.method === 'STOMP' && wsConnected" class="stomp-send-section">
            <div class="stomp-section-title">发送消息</div>
            <el-input
              v-model="stompSendDestination"
              placeholder="消息主题 (destination)"
              class="stomp-destination-input"
            />
            <el-input
              v-model="stompMessageContent"
              type="textarea"
              :rows="4"
              placeholder="消息内容"
              class="stomp-message-input"
            />
            <el-button type="primary" size="small" @click="sendStompMessage">
              发送
            </el-button>
          </div>

          <!-- STOMP 订阅区域 -->
          <div v-if="localApi.method === 'STOMP' && wsConnected" class="stomp-subscribe-section">
            <div class="stomp-section-title">订阅主题</div>
            <div class="stomp-subscribe-input-group">
              <el-input
                v-model="stompSubscribeDestination"
                placeholder="订阅主题 (destination)"
                class="stomp-destination-input"
              />
              <el-button type="primary" size="small" @click="subscribeStomp">
                订阅
              </el-button>
            </div>
            <div class="stomp-subscriptions">
              <el-tag
                v-for="sub in stompSubscriptions"
                :key="sub.id"
                closable
                @close="unsubscribeStomp(sub.id)"
              >
                {{ sub.destination }}
              </el-tag>
            </div>
          </div>

          <!-- WebSocket 发送区域 -->
          <div v-if="localApi.method === 'WS' && wsConnected" class="ws-send-section">
            <el-input
              v-model="wsMessageContent"
              type="textarea"
              :rows="4"
              placeholder="消息内容"
              class="ws-message-input"
            />
            <el-button type="primary" size="small" @click="sendWsMessage">
              发送消息
            </el-button>
          </div>

          <div class="ws-message-list">
            <div v-if="wsMessages.length === 0" class="ws-empty">
              <el-empty description="暂无消息" />
            </div>
            <div
              v-for="msg in wsMessages"
              :key="msg.id"
              class="ws-message"
              :class="'ws-message-' + msg.type"
            >
              <div class="ws-message-header">
                <span class="ws-message-from">{{ msg.from }}</span>
                <span class="ws-message-time">{{ msg.time }}</span>
              </div>
              <div class="ws-message-content">
                <pre>{{ msg.content }}</pre>
              </div>
            </div>
          </div>
        </div>

        <el-alert
          v-if="error"
          :title="error"
          type="error"
          :closable="false"
          show-icon
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { Promotion, CircleClose, Upload } from '@element-plus/icons-vue'
import axios from 'axios'

// eslint-disable-next-line no-undef
const props = defineProps({
  api: {
    type: Object,
    required: true
  },
  environments: {
    type: Array,
    default: () => []
  },
  currentEnvId: {
    type: String,
    default: ''
  }
})

// eslint-disable-next-line no-undef
const emit = defineEmits(['update'])

// 本地副本
const localApi = reactive({
  ...props.api,
  responseBody: props.api.responseBody || { type: 'none', content: '' },
  body: props.api.body || { type: 'none', content: '', formData: [] }
})

// 监听 api 变化
watch(() => props.api, (newApi) => {
  Object.assign(localApi, {
    ...newApi,
    responseBody: newApi.responseBody || { type: 'none', content: '' },
    body: newApi.body || { type: 'none', content: '', formData: [] }
  })
}, { deep: true })

const activeTab = ref('params')
const responseTab = ref('body')
const loading = ref(false)
const response = ref(null)
const error = ref('')

// WebSocket 相关状态
const wsConnected = ref(false)
const wsConnection = ref(null)
const stompClient = ref(null)
const wsMessages = ref([])
const wsMessageContent = ref('')
const isWebSocketMethod = computed(() => {
  return localApi.method === 'WS' || localApi.method === 'STOMP'
})

// STOMP 相关状态
const stompSendDestination = ref('')
const stompMessageContent = ref('')
const stompSubscribeDestination = ref('')
const stompSubscriptions = ref([])

// 更新接口数据
const handleUpdate = () => {
  emit('update', { ...localApi })
}

// 添加参数
const addParam = () => {
  localApi.params.push({ key: '', value: '', description: '' })
  handleUpdate()
}

// 删除参数
const removeParam = (index) => {
  localApi.params.splice(index, 1)
  handleUpdate()
}

// 添加 Header
const addHeader = () => {
  localApi.headers.push({ key: '', value: '', description: '' })
  handleUpdate()
}

// 删除 Header
const removeHeader = (index) => {
  localApi.headers.splice(index, 1)
  handleUpdate()
}

// Body 类型切换处理
const handleBodyTypeChange = () => {
  if (!localApi.body.formData) {
    localApi.body.formData = []
  }
  if (['form', 'x-www-form-urlencoded'].includes(localApi.body.type) && localApi.body.formData.length === 0) {
    localApi.body.formData.push({ key: '', value: '', type: 'text', description: '' })
  }
  handleUpdate()
}

// 添加 form-data 项
const addFormDataItem = () => {
  if (!localApi.body.formData) {
    localApi.body.formData = []
  }
  localApi.body.formData.push({ key: '', value: '', type: localApi.body.type === 'form' ? 'text' : 'text', description: '' })
  handleUpdate()
}

// 删除 form-data 项
const removeFormDataItem = (index) => {
  localApi.body.formData.splice(index, 1)
  handleUpdate()
}

// 文件选择处理（form-data）
const handleFileChange = (file, index) => {
  localApi.body.formData[index].file = file.raw
  localApi.body.formData[index].fileName = file.name
  handleUpdate()
}

// binary 文件选择处理
const handleBinaryFileChange = (file) => {
  localApi.body.file = file.raw
  localApi.body.fileName = file.name
  handleUpdate()
}

// 清除 binary 文件
const clearBinaryFile = () => {
  localApi.body.file = null
  localApi.body.fileName = ''
  handleUpdate()
}

// 添加响应参数
const addResponseParam = () => {
  if (!localApi.responseParams) {
    localApi.responseParams = []
  }
  localApi.responseParams.push({ key: '', type: 'string', required: false, value: '', description: '' })
  handleUpdate()
}

// 删除响应参数
const removeResponseParam = (index) => {
  localApi.responseParams.splice(index, 1)
  handleUpdate()
}

// 添加响应头
const addResponseHeader = () => {
  if (!localApi.responseHeaders) {
    localApi.responseHeaders = []
  }
  localApi.responseHeaders.push({ key: '', value: '', description: '' })
  handleUpdate()
}

// 删除响应头
const removeResponseHeader = (index) => {
  localApi.responseHeaders.splice(index, 1)
  handleUpdate()
}

// 检查是否包含环境变量
const hasEnvVariables = (str) => {
  if (!str) return false
  return /\{\{[^}]+\}\}/.test(str)
}

// 替换环境变量
const replaceEnvVariables = (str) => {
  if (!str || !props.currentEnvId || !props.environments.length) {
    return str
  }

  const currentEnv = props.environments.find(e => e.id === props.currentEnvId)
  if (!currentEnv || !currentEnv.variables) {
    return str
  }

  let result = str
  currentEnv.variables.forEach(variable => {
    if (variable.key && variable.value) {
      const regex = new RegExp(`\\{\\{\\s*${variable.key}\\s*\\}\\}`, 'g')
      result = result.replace(regex, variable.value)
    }
  })

  return result
}

// 处理方法切换
const handleMethodChange = () => {
  // 如果切换到 WebSocket/STOMP，清空之前的响应
  if (isWebSocketMethod.value) {
    response.value = null
    error.value = ''
  }
  handleUpdate()
}

// WebSocket 连接
const connectWebSocket = async () => {
  if (!localApi.url) {
    ElMessage.warning('请输入 WebSocket URL')
    return
  }

  const url = replaceEnvVariables(localApi.url)
  wsMessages.value = []

  try {
    if (localApi.method === 'STOMP') {
      // STOMP 连接
      await connectSTOMP(url)
    } else {
      // 原生 WebSocket 连接
      await connectNativeWS(url)
    }
  } catch (err) {
    error.value = '连接失败: ' + err.message
    ElMessage.error('连接失败: ' + err.message)
  }
}

// 原生 WebSocket 连接
const connectNativeWS = (url) => {
  return new Promise((resolve, reject) => {
    const ws = new WebSocket(url)

    ws.onopen = () => {
      wsConnected.value = true
      wsConnection.value = ws
      ElMessage.success('WebSocket 连接成功')
      addWsMessage('系统', '连接成功', 'info')
      resolve()
    }

    ws.onmessage = (event) => {
      addWsMessage('服务器', event.data, 'receive')
    }

    ws.onerror = (err) => {
      error.value = 'WebSocket 错误'
      addWsMessage('系统', '连接错误', 'error')
      reject(err)
    }

    ws.onclose = () => {
      wsConnected.value = false
      wsConnection.value = null
      addWsMessage('系统', '连接已关闭', 'info')
    }
  })
}

// STOMP 连接
const connectSTOMP = (url) => {
  return new Promise((resolve, reject) => {
    // 动态导入 @stomp/stompjs
    import('@stomp/stompjs').then(({ Client }) => {
      const client = new Client({
        brokerURL: url,
        connectHeaders: {},
        debug: (str) => {
          console.log('STOMP: ' + str)
        },
        reconnectDelay: 5000,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000
      })

      client.onConnect = () => {
        wsConnected.value = true
        stompClient.value = client
        ElMessage.success('STOMP 连接成功')
        addWsMessage('系统', 'STOMP 连接成功', 'info')
        resolve()
      }

      client.onStompError = (frame) => {
        const errorMsg = frame.headers?.message || '未知错误'
        error.value = 'STOMP 错误: ' + errorMsg
        addWsMessage('系统', 'STOMP 错误: ' + errorMsg, 'error')
        reject(new Error(errorMsg))
      }

      client.onWebSocketClose = () => {
        wsConnected.value = false
        stompClient.value = null
        addWsMessage('系统', 'STOMP 连接已关闭', 'info')
      }

      client.activate()
    }).catch(reject)
  })
}

// 断开 WebSocket 连接
const disconnectWebSocket = () => {
  // 清理 STOMP 订阅
  if (localApi.method === 'STOMP' && stompSubscriptions.value.length > 0) {
    stompSubscriptions.value.forEach(sub => {
      if (sub.subscription) {
        sub.subscription.unsubscribe()
      }
    })
    stompSubscriptions.value = []
  }

  if (localApi.method === 'STOMP' && stompClient.value) {
    stompClient.value.deactivate()
    stompClient.value = null
  } else if (wsConnection.value) {
    wsConnection.value.close()
    wsConnection.value = null
  }
  wsConnected.value = false
  ElMessage.success('已断开连接')
}

// 添加 WebSocket 消息
const addWsMessage = (from, content, type) => {
  wsMessages.value.push({
    id: Date.now(),
    from,
    content,
    type,
    time: new Date().toLocaleTimeString()
  })
}

// 发送 WebSocket 消息
const sendWsMessage = () => {
  if (!wsMessageContent.value) {
    ElMessage.warning('请输入要发送的消息')
    return
  }

  const message = replaceEnvVariables(wsMessageContent.value)

  if (wsConnection.value) {
    // 原生 WebSocket 发送消息
    wsConnection.value.send(message)
    addWsMessage('客户端', message, 'send')
    wsMessageContent.value = ''
  }
}

// 发送 STOMP 消息
const sendStompMessage = () => {
  if (!stompSendDestination.value) {
    ElMessage.warning('请输入消息主题')
    return
  }
  if (!stompMessageContent.value) {
    ElMessage.warning('请输入消息内容')
    return
  }

  const destination = replaceEnvVariables(stompSendDestination.value)
  const message = replaceEnvVariables(stompMessageContent.value)

  if (stompClient.value) {
    stompClient.value.publish({
      destination: destination,
      body: message
    })
    addWsMessage('客户端 → ' + destination, message, 'send')
    stompMessageContent.value = ''
  }
}

// 订阅 STOMP 主题
const subscribeStomp = () => {
  if (!stompSubscribeDestination.value) {
    ElMessage.warning('请输入订阅主题')
    return
  }

  const destination = replaceEnvVariables(stompSubscribeDestination.value)

  if (stompClient.value) {
    const subscription = stompClient.value.subscribe(destination, (message) => {
      addWsMessage('服务器 ← ' + destination, message.body, 'receive')
    })

    stompSubscriptions.value.push({
      id: subscription.id,
      destination: destination,
      subscription: subscription
    })

    ElMessage.success('已订阅: ' + destination)
    stompSubscribeDestination.value = ''
  }
}

// 取消订阅
const unsubscribeStomp = (id) => {
  const sub = stompSubscriptions.value.find(s => s.id === id)
  if (sub && sub.subscription) {
    sub.subscription.unsubscribe()
    stompSubscriptions.value = stompSubscriptions.value.filter(s => s.id !== id)
    ElMessage.success('已取消订阅: ' + sub.destination)
  }
}

// 发送请求
const sendRequest = async () => {
  if (!localApi.url) {
    ElMessage.warning('请输入请求 URL')
    return
  }

  // WebSocket/STOMP 请求 - 直接发送消息
  if (isWebSocketMethod.value) {
    if (wsConnected.value) {
      sendWsMessage()
    } else {
      ElMessage.warning('请先连接 WebSocket')
    }
    return
  }

  loading.value = true
  response.value = null
  error.value = ''

  const startTime = Date.now()

  try {
    // 构建 URL（替换环境变量）
    let url = replaceEnvVariables(localApi.url)
    const params = {}
    localApi.params.forEach(p => {
      if (p.key) {
        params[p.key] = replaceEnvVariables(p.value)
      }
    })

    // 构建 Headers（替换环境变量）
    const headers = {}
    localApi.headers.forEach(h => {
      if (h.key) {
        headers[h.key] = replaceEnvVariables(h.value)
      }
    })

    // 构建请求配置
    const config = {
      method: localApi.method,
      url: url,
      params: params,
      headers: headers,
      timeout: 30000
    }

    // 处理 Body
    if (localApi.body.type !== 'none' && localApi.body.content) {
      if (localApi.body.type === 'json') {
        headers['Content-Type'] = 'application/json'
        config.data = JSON.parse(localApi.body.content)
      } else {
        config.data = localApi.body.content
      }
    }

    const res = await axios(config)

    const endTime = Date.now()

    response.value = {
      status: res.status,
      statusText: res.statusText,
      time: endTime - startTime,
      size: JSON.stringify(res.data).length,
      body: res.data,
      headers: res.headers
    }
  } catch (err) {
    const endTime = Date.now()

    if (err.response) {
      response.value = {
        status: err.response.status,
        statusText: err.response.statusText,
        time: endTime - startTime,
        size: JSON.stringify(err.response.data).length,
        body: err.response.data,
        headers: err.response.headers
      }
    } else {
      error.value = err.message || '请求失败'
    }
  } finally {
    loading.value = false
  }
}

// 获取状态码类型
const getStatusType = (status) => {
  if (status >= 200 && status < 300) return 'success'
  if (status >= 300 && status < 400) return 'warning'
  if (status >= 400) return 'danger'
  return 'info'
}

// 格式化大小
const formatSize = (size) => {
  if (size < 1024) return size + ' B'
  if (size < 1024 * 1024) return (size / 1024).toFixed(2) + ' KB'
  return (size / (1024 * 1024)).toFixed(2) + ' MB'
}

// 判断是否为 JSON
const isJson = (data) => {
  if (typeof data === 'object') return true
  try {
    JSON.parse(data)
    return true
  } catch {
    return false
  }
}

// 格式化 JSON
const formatJson = (data) => {
  if (typeof data === 'object') {
    return JSON.stringify(data, null, 2)
  }
  try {
    return JSON.stringify(JSON.parse(data), null, 2)
  } catch {
    return data
  }
}
</script>

<style scoped>
.api-workspace {
  display: flex;
  flex-direction: column;
}

.request-section {
  border-bottom: 1px solid #e4e7ed;
}

.api-header {
  margin-bottom: 15px;
}

.api-name-input {
  font-size: 16px;
}

.request-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.url-input {
  flex: 1;
}

.request-url {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 15px;
  font-size: 13px;
}

.resolved-url {
  color: #409eff;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  word-break: break-all;
}

.request-tabs {
  margin-top: 10px;
}

.params-table {
  padding: 10px 0;
}

.table-header {
  display: flex;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #e4e7ed;
  font-weight: 600;
  color: #999;
}

.table-row {
  display: flex;
  gap: 10px;
  padding: 8px 0;
  align-items: center;
}

.col-key {
  width: 180px;
}

.col-value {
  width: 200px;
}

.col-type {
  width: 120px;
}

.col-required {
  width: 60px;
  display: flex;
  justify-content: center;
}

.col-desc {
  flex: 1;
}

.col-action {
  width: 60px;
  text-align: center;
}

.body-section {
  padding: 10px 0;
}

.body-editor {
  margin-top: 15px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.file-upload {
  display: flex;
  align-items: center;
}

.binary-upload {
  margin-top: 15px;
  padding: 20px;
  border: 2px dashed #dcdfe6;
  border-radius: 4px;
  text-align: center;
}

.binary-upload:hover {
  border-color: #409eff;
}

.selected-file {
  margin-top: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.response-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.response-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  border-bottom: 1px solid #e4e7ed;
  background-color: #f5f7fa;
}

.response-title {
  font-weight: 600;
  font-size: 14px;
  color: #303133;
}

.response-meta {
  display: flex;
  gap: 10px;
}

.response-content {
  flex: 1;
  overflow: auto;
}

.loading-state {
  padding: 20px;
}

.response-data {
  height: 100%;
}

.response-body {
  background-color: #f5f7fa;
  padding: 15px;
  border-radius: 4px;
  overflow: auto;
}

.response-body pre {
  margin: 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.response-headers {
  background-color: #f5f7fa;
  padding: 15px;
  border-radius: 4px;
}

.header-row {
  padding: 5px 0;
  border-bottom: 1px solid #e4e7ed;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
}

.header-row:last-child {
  border-bottom: none;
}

.header-key {
  color: #409EFF;
  font-weight: 600;
  margin-right: 10px;
}

.header-value {
  color: #67C23A;
}

/* WebSocket 消息样式 */
.ws-messages {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.ws-status {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #e4e7ed;
}

.ws-message-list {
  flex: 1;
  overflow: auto;
  padding: 10px 0;
}

.ws-empty {
  padding: 40px 0;
}

.ws-message {
  margin-bottom: 15px;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}

.ws-message-info {
  background-color: #f4f4f5;
  border-color: #e9e9eb;
}

.ws-message-send {
  background-color: #ecf5ff;
  border-color: #d9ecff;
  margin-left: 20%;
}

.ws-message-receive {
  background-color: #f0f9eb;
  border-color: #e1f3d8;
  margin-right: 20%;
}

.ws-message-error {
  background-color: #fef0f0;
  border-color: #fde2e2;
}

.ws-message-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 12px;
}

.ws-message-from {
  font-weight: 600;
  color: #606266;
}

.ws-message-time {
  color: #909399;
}

.ws-message-content {
  font-size: 13px;
}

.ws-message-content pre {
  margin: 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* STOMP 样式 */
.stomp-send-section,
.stomp-subscribe-section,
.ws-send-section {
  padding: 15px;
  border-bottom: 1px solid #e4e7ed;
  background-color: #fafafa;
}

.stomp-section-title {
  font-weight: 600;
  font-size: 14px;
  color: #303133;
  margin-bottom: 10px;
}

.stomp-destination-input {
  margin-bottom: 10px;
}

.stomp-message-input,
.ws-message-input {
  margin-bottom: 10px;
}

.stomp-subscribe-input-group {
  display: flex;
  gap: 10px;
}

.stomp-subscribe-input-group .el-input {
  flex: 1;
}

.stomp-subscriptions {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
</style>
